import React from "react";
import ReactDOM from "react-dom";
import { Transition } from "@tailwindui/react";

/**
 * Modal - reusable animated modal shell with backdrop and panel.
 *
 * Provides: portal mounting, backdrop fade, panel scale/fade animation.
 * Callers control panel sizing and content via panelClassName + children.
 *
 * @param {boolean} show - modal visibility
 * @param {function} onClose - called when backdrop is clicked
 * @param {React.ReactNode} children - content rendered inside the white panel
 * @param {string} panelClassName - extra classes on the panel (width, overflow, etc.)
 * @param {"center"|"bottom"} position - vertical alignment (default "center")
 * @param {string} zIndex - z-index class for the container (default "z-50")
 * @param {boolean} useOpacityOnlyTransition - skip transform animation (for drag-and-drop compat)
 */
const Modal = ({
  show,
  onClose,
  children,
  panelClassName = "",
  position = "center",
  zIndex = "z-50",
  useOpacityOnlyTransition = false
}) => {
  const isCenter = position === "center";

  const panelEnterFrom = useOpacityOnlyTransition
    ? "opacity-0"
    : "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95";
  const panelEnterTo = useOpacityOnlyTransition
    ? "opacity-100"
    : "opacity-100 translate-y-0 sm:scale-100";
  const panelLeaveFrom = useOpacityOnlyTransition
    ? "opacity-100"
    : "opacity-100 translate-y-0 sm:scale-100";
  const panelLeaveTo = useOpacityOnlyTransition
    ? "opacity-0"
    : "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95";

  return ReactDOM.createPortal(
    <Transition show={show}>
      <div className={`fixed ${zIndex} inset-0 overflow-y-auto`}>
        <div
          className={
            isCenter
              ? "flex items-center justify-center my-6 min-h-screen"
              : "flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
          }
        >
          {/* Backdrop */}
          <Transition.Child
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            as="div"
            className={`fixed inset-0 ${isCenter ? "z-40" : ""} transition-opacity`}
          >
            <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose} />
          </Transition.Child>

          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" />
          &#8203;

          {/* Panel */}
          <Transition.Child
            enter="ease-out duration-300"
            enterFrom={panelEnterFrom}
            enterTo={panelEnterTo}
            leave="ease-in duration-200"
            leaveFrom={panelLeaveFrom}
            leaveTo={panelLeaveTo}
            as="div"
            role="dialog"
            aria-modal="true"
            className={`${isCenter ? "relative z-50" : ""} inline-block ${
              isCenter ? "align-middle" : "align-bottom"
            } bg-white rounded-lg text-left shadow-xl transition-all ${
              useOpacityOnlyTransition ? "" : "transform"
            } ${panelClassName}`}
          >
            {children}
          </Transition.Child>
        </div>
      </div>
    </Transition>,
    document.body
  );
};

export default Modal;
